import React, { useState, useEffect } from 'react';
import { ArrowLeftRight, History, Calculator, Box, Copy, Check } from 'lucide-react';

interface HistoryEntry {
  id: number;
  from: string;
  to: string;
  timestamp: string;
}

interface UnitDefinition {
  factor: number;
  category: string;
  symbol: string;
  fullName: string;
  description: string;
}

type UnitName = 'cubic_millimeters' | 'cubic_centimeters' | 'milliliters' | 'centiliters' | 'deciliters' | 'liters' | 'cubic_meters' | 'cubic_kilometers' | 'cubic_inches' | 'fluid_ounces_us' | 'cups_us' | 'pints_us' | 'quarts_us' | 'gallons_us' | 'cubic_feet' | 'cubic_yards' | 'fluid_ounces_uk' | 'pints_uk' | 'gallons_uk' | 'teaspoons' | 'tablespoons' | 'barrels_oil' | 'wine_bottles' | 'beer_bottles' | 'bushels_us' | 'acre_feet';

const VolumeConverter: React.FC = () => {
  const [fromValue, setFromValue] = useState<string>('');
  const [toValue, setToValue] = useState<string>('');
  const [fromUnit, setFromUnit] = useState<UnitName>('liters');
  const [toUnit, setToUnit] = useState<UnitName>('gallons_us');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [precision, setPrecision] = useState<number>(4);

  const units: Record<UnitName, UnitDefinition> = {
    // Metric
    'cubic_millimeters': { 
      factor: 1e-6, 
      category: 'Metric', 
      symbol: 'mm³', 
      fullName: 'Cubic Millimeters',
      description: 'Very small volumes, droplets'
    },
    'cubic_centimeters': { 
      factor: 0.001, 
      category: 'Metric', 
      symbol: 'cm³', 
      fullName: 'Cubic Centimeters',
      description: 'Small volumes, medical dosages'
    },
    'milliliters': { 
      factor: 0.001, 
      category: 'Metric', 
      symbol: 'mL', 
      fullName: 'Milliliters',
      description: 'Small liquid volumes, medicine'
    },
    'centiliters': { 
      factor: 0.01, 
      category: 'Metric', 
      symbol: 'cL', 
      fullName: 'Centiliters',
      description: 'Small beverage servings'
    },
    'deciliters': { 
      factor: 0.1, 
      category: 'Metric', 
      symbol: 'dL', 
      fullName: 'Deciliters',
      description: 'Medium liquid volumes'
    },
    'liters': { 
      factor: 1, 
      category: 'Metric', 
      symbol: 'L', 
      fullName: 'Liters',
      description: 'Standard metric volume unit'
    },
    'cubic_meters': { 
      factor: 1000, 
      category: 'Metric', 
      symbol: 'm³', 
      fullName: 'Cubic Meters',
      description: 'Large volumes, room capacity'
    },
    'cubic_kilometers': { 
      factor: 1e12, 
      category: 'Metric', 
      symbol: 'km³', 
      fullName: 'Cubic Kilometers',
      description: 'Geological volumes'
    },
    
    // US Customary
    'cubic_inches': { 
      factor: 0.016387064, 
      category: 'US Customary', 
      symbol: 'cu in', 
      fullName: 'Cubic Inches',
      description: 'Engine displacement, small volumes'
    },
    'fluid_ounces_us': { 
      factor: 0.0295735, 
      category: 'US Customary', 
      symbol: 'fl oz', 
      fullName: 'US Fluid Ounces',
      description: 'Small liquid measurements'
    },
    'cups_us': { 
      factor: 0.236588, 
      category: 'US Customary', 
      symbol: 'cup', 
      fullName: 'US Cups',
      description: 'Cooking measurements'
    },
    'pints_us': { 
      factor: 0.473176, 
      category: 'US Customary', 
      symbol: 'pt', 
      fullName: 'US Pints',
      description: 'Beverage volumes'
    },
    'quarts_us': { 
      factor: 0.946353, 
      category: 'US Customary', 
      symbol: 'qt', 
      fullName: 'US Quarts',
      description: 'Medium liquid volumes'
    },
    'gallons_us': { 
      factor: 3.78541, 
      category: 'US Customary', 
      symbol: 'gal', 
      fullName: 'US Gallons',
      description: 'Fuel, large liquid volumes'
    },
    'cubic_feet': { 
      factor: 28.3168, 
      category: 'US Customary', 
      symbol: 'cu ft', 
      fullName: 'Cubic Feet',
      description: 'Room volumes, gas measurements'
    },
    'cubic_yards': { 
      factor: 764.555, 
      category: 'US Customary', 
      symbol: 'cu yd', 
      fullName: 'Cubic Yards',
      description: 'Construction materials'
    },
    
    // Imperial
    'fluid_ounces_uk': { 
      factor: 0.0284131, 
      category: 'Imperial', 
      symbol: 'fl oz (UK)', 
      fullName: 'UK Fluid Ounces',
      description: 'British liquid measurements'
    },
    'pints_uk': { 
      factor: 0.568261, 
      category: 'Imperial', 
      symbol: 'pt (UK)', 
      fullName: 'UK Pints',
      description: 'British beer volumes'
    },
    'gallons_uk': { 
      factor: 4.54609, 
      category: 'Imperial', 
      symbol: 'gal (UK)', 
      fullName: 'UK Gallons',
      description: 'British fuel volumes'
    },
    
    // Cooking
    'teaspoons': { 
      factor: 0.00492892, 
      category: 'Cooking', 
      symbol: 'tsp', 
      fullName: 'Teaspoons',
      description: 'Small cooking measurements'
    },
    'tablespoons': { 
      factor: 0.0147868, 
      category: 'Cooking', 
      symbol: 'tbsp', 
      fullName: 'Tablespoons',
      description: 'Cooking measurements'
    },
    
    // Industrial
    'barrels_oil': { 
      factor: 158.987, 
      category: 'Industrial', 
      symbol: 'bbl', 
      fullName: 'Oil Barrels',
      description: 'Petroleum industry'
    },
    
    // Beverage
    'wine_bottles': { 
      factor: 0.75, 
      category: 'Beverage', 
      symbol: 'bottle', 
      fullName: 'Wine Bottles',
      description: 'Standard wine bottle'
    },
    'beer_bottles': { 
      factor: 0.355, 
      category: 'Beverage', 
      symbol: 'beer', 
      fullName: 'Beer Bottles',
      description: 'Standard beer bottle'
    },
    
    // Agricultural
    'bushels_us': { 
      factor: 35.2391, 
      category: 'Agricultural', 
      symbol: 'bu', 
      fullName: 'US Bushels',
      description: 'Grain measurement'
    },
    
    // Specialized
    'acre_feet': { 
      factor: 1233482, 
      category: 'Specialized', 
      symbol: 'ac⋅ft', 
      fullName: 'Acre-Feet',
      description: 'Water reservoir measurement'
    }
  };

  const categories = Array.from(new Set(Object.values(units).map(unit => unit.category)));

  const convert = (value: number, from: UnitName, to: UnitName): number => {
    if (!value && value !== 0 || isNaN(value)) return 0;
    const fromFactor = units[from].factor;
    const toFactor = units[to].factor;
    const result = (value * fromFactor) / toFactor;
    return parseFloat(result.toPrecision(precision));
  };

  const handleFromValueChange = (value: string): void => {
    setFromValue(value);
    if ((value || value === '0') && !isNaN(Number(value))) {
      const converted = convert(parseFloat(value), fromUnit, toUnit);
      setToValue(converted.toString());
    } else {
      setToValue('');
    }
  };

  const handleToValueChange = (value: string): void => {
    setToValue(value);
    if ((value || value === '0') && !isNaN(Number(value))) {
      const converted = convert(parseFloat(value), toUnit, fromUnit);
      setFromValue(converted.toString());
    } else {
      setFromValue('');
    }
  };

  const swapUnits = (): void => {
    const tempUnit = fromUnit;
    const tempValue = fromValue;
    
    setFromUnit(toUnit);
    setToUnit(tempUnit);
    setFromValue(toValue);
    setToValue(tempValue);
  };

  const addToHistory = (): void => {
    if ((fromValue || fromValue === '0') && (toValue || toValue === '0')) {
      const entry: HistoryEntry = {
        id: Date.now(),
        from: `${fromValue} ${units[fromUnit].symbol}`,
        to: `${toValue} ${units[toUnit].symbol}`,
        timestamp: new Date().toLocaleTimeString()
      };
      setHistory(prev => [entry, ...prev.slice(0, 9)]);
    }
  };

  const copyResult = (): void => {
    if (toValue || toValue === '0') {
      navigator.clipboard.writeText(`${toValue} ${units[toUnit].symbol}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const clearAll = (): void => {
    setFromValue('');
    setToValue('');
  };

  const getVolumeVisualization = (volume: number, unit: UnitName): string[] => {
    const liters = volume * units[unit].factor;
    const visualizations: string[] = [];
    
    if (liters < 0.001) visualizations.push('Smaller than a drop');
    else if (liters < 0.01) visualizations.push('Few drops');
    else if (liters < 0.1) visualizations.push('Medicine dose');
    else if (liters < 0.5) visualizations.push('Small can');
    else if (liters < 1) visualizations.push('Large bottle');
    else if (liters < 5) visualizations.push('Large water bottle');
    else if (liters < 20) visualizations.push('Water jug');
    else if (liters < 100) visualizations.push('Large container');
    else if (liters < 1000) visualizations.push('Bathtub size');
    else visualizations.push('Large tank');
    
    return visualizations;
  };

  const getCommonEquivalents = (volume: number, unit: UnitName): string[] => {
    const liters = volume * units[unit].factor;
    const equivalents: string[] = [];
    
    if (unit !== 'gallons_us' && liters > 0.1) {
      const gallons = liters / units.gallons_us.factor;
      equivalents.push(`≈ ${gallons.toFixed(2)} US gal`);
    }
    
    if (unit !== 'cups_us' && liters > 0.1 && liters < 10) {
      const cups = liters / units.cups_us.factor;
      equivalents.push(`≈ ${cups.toFixed(1)} cups`);
    }
    
    return equivalents.slice(0, 2);
  };

  useEffect(() => {
    if ((fromValue || fromValue === '0') && !isNaN(Number(fromValue))) {
      const converted = convert(parseFloat(fromValue), fromUnit, toUnit);
      setToValue(converted.toString());
    }
  }, [fromUnit, toUnit, precision, fromValue]);

  const renderUnitOptions = (): React.ReactElement[] => {
    return categories.map(category => (
      <optgroup key={category} label={category}>
        {Object.entries(units)
          .filter(([_, unit]) => unit.category === category)
          .map(([unitName, unit]) => (
            <option key={unitName} value={unitName}>
              {unit.fullName} ({unit.symbol})
            </option>
          ))}
      </optgroup>
    ));
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-cyan-100 min-h-screen">
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Box className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-800">Advanced Volume Converter</h1>
          </div>
          <p className="text-gray-600">Convert between multiple volume units with precision</p>
        </div>

        <div className="flex flex-wrap gap-4 mb-6 justify-center">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Precision:</label>
            <select 
              value={precision} 
              onChange={(e) => setPrecision(parseInt(e.target.value))}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {[2, 3, 4, 5, 6, 8, 10].map(p => (
                <option key={p} value={p}>{p} digits</option>
              ))}
            </select>
          </div>
          
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <History className="w-4 h-4" />
            History
          </button>
          
          <button
            onClick={clearAll}
            className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
          >
            Clear All
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              From
            </h3>
            <div className="space-y-3">
              <input
                type="number"
                value={fromValue}
                onChange={(e) => handleFromValueChange(e.target.value)}
                placeholder="Enter volume"
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value as UnitName)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {renderUnitOptions()}
              </select>
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                {units[fromUnit].description}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              To
            </h3>
            <div className="space-y-3">
              <div className="relative">
                <input
                  type="number"
                  value={toValue}
                  onChange={(e) => handleToValueChange(e.target.value)}
                  placeholder="Result"
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                />
                <button
                  onClick={copyResult}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Copy result"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                </button>
              </div>
              <select
                value={toUnit}
                onChange={(e) => setToUnit(e.target.value as UnitName)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {renderUnitOptions()}
              </select>
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                {units[toUnit].description}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center mb-6">
          <button
            onClick={swapUnits}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-md"
          >
            <ArrowLeftRight className="w-5 h-5" />
            Swap Units
          </button>
        </div>

        {(fromValue || fromValue === '0') && (toValue || toValue === '0') && (
          <div className="flex justify-center mb-6">
            <button
              onClick={addToHistory}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              Add to History
            </button>
          </div>
        )}

        {(fromValue || fromValue === '0') && (toValue || toValue === '0') && (
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-2">Conversion Summary</h4>
            <p className="text-lg mb-3">
              <span className="font-mono bg-white px-2 py-1 rounded">{fromValue} {units[fromUnit].symbol}</span>
              <span className="mx-3 text-gray-500">=</span>
              <span className="font-mono bg-white px-2 py-1 rounded font-semibold text-blue-700">{toValue} {units[toUnit].symbol}</span>
            </p>
            
            {fromValue && !isNaN(Number(fromValue)) && (
              <div className="space-y-2">
                <div className="text-sm text-gray-600">
                  <strong>Size Comparison:</strong>
                  {getVolumeVisualization(parseFloat(fromValue), fromUnit).map(viz => (
                    <span key={viz} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mr-2 mb-1">
                      {viz}
                    </span>
                  ))}
                </div>
                
                <div className="text-sm text-gray-600">
                  <strong>Common Equivalents:</strong>
                  {getCommonEquivalents(parseFloat(fromValue), fromUnit).map(equiv => (
                    <span key={equiv} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs mr-2 mb-1">
                      {equiv}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-gray-800 mb-3">Common Volume References</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="space-y-1">
              <h5 className="font-medium text-blue-800">Cooking</h5>
              <div>1 tsp = 4.93 mL</div>
              <div>1 tbsp = 14.79 mL</div>
              <div>1 cup = 236.6 mL</div>
            </div>
            <div className="space-y-1">
              <h5 className="font-medium text-green-800">Beverages</h5>
              <div>Beer can = 355 mL</div>
              <div>Wine bottle = 750 mL</div>
              <div>2L soda = 2000 mL</div>
            </div>
            <div className="space-y-1">
              <h5 className="font-medium text-purple-800">Fuel</h5>
              <div>US gallon = 3.785 L</div>
              <div>UK gallon = 4.546 L</div>
              <div>Oil barrel = 159 L</div>
            </div>
            <div className="space-y-1">
              <h5 className="font-medium text-red-800">Large Volumes</h5>
              <div>Bathtub = ~300 L</div>
              <div>Hot tub = ~1,500 L</div>
              <div>Pool = ~50,000 L</div>
            </div>
          </div>
        </div>

        {showHistory && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <History className="w-5 h-5" />
              Conversion History
            </h4>
            {history.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No conversions in history</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((entry) => (
                  <div key={entry.id} className="flex justify-between items-center bg-white p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm">{entry.from}</span>
                      <ArrowLeftRight className="w-4 h-4 text-gray-400" />
                      <span className="font-mono text-sm font-semibold">{entry.to}</span>
                    </div>
                    <span className="text-xs text-gray-500">{entry.timestamp}</span>
                  </div>
                ))}
              </div>
            )}
            {history.length > 0 && (
              <button
                onClick={() => setHistory([])}
                className="mt-3 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors text-sm"
              >
                Clear History
              </button>
            )}
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map(category => (
            <div key={category} className="bg-gray-50 rounded-lg p-4">
              <h5 className="font-semibold text-gray-800 mb-2">{category}</h5>
              <div className="space-y-1">
                {Object.entries(units)
                  .filter(([_, unit]) => unit.category === category)
                  .slice(0, 4)
                  .map(([unitName, unit]) => (
                    <div key={unitName} className="text-sm text-gray-600">
                      {unit.symbol} - {unit.fullName}
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VolumeConverter;